import React, { useRef, useState } from 'react';
import PopupComponent from '../../components/PopupComponent';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import copy from 'copy-to-clipboard';
import toast from 'react-hot-toast';
import styled from 'styled-components';

interface ShareFormPopupProps {
  isOpen: boolean;
  onClose: () => void;
  formLink: string;
  formName: string;
}

const ShareFormPopup: React.FC<ShareFormPopupProps> = ({
  isOpen,
  onClose,
  formLink,
  formName,
}) => {
  const [copied, setCopied] = useState(false);
  const qrRef = useRef<HTMLDivElement>(null);

  const handleCopyLink = () => {
    copy(formLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadQR = () => {
    // Find the canvas element inside the ref
    const canvas = qrRef.current?.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formName.replace(/\s+/g, '_')}_QR.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('QR code downloaded!');
    }
  };

  return (
    <PopupComponent
      isOpen={isOpen}
      onClose={onClose}
      title="Share Form"
      size="sm"
    >
      <ShareContainer>
        <QRSection ref={qrRef}>
          <QRCodeCanvas
            value={formLink}
            size={200}
            level="H"
            includeMargin={true}
            bgColor="#ffffff"
            fgColor="#000000"
          />
        </QRSection>

        <FormNameLabel>{formName}</FormNameLabel>

        <LinkSection>
          <LinkInput
            type="text"
            value={formLink}
            readOnly
            onClick={(e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()}
          />
          <CopyButton onClick={handleCopyLink} $copied={copied}>
            <img
              src="assets/img/copy.png"
              alt="copy"
              style={{ width: '14px', marginRight: '6px' }}
            />
            {copied ? 'Copied!' : 'Copy Link'}
          </CopyButton>
        </LinkSection>

        <DownloadButton onClick={handleDownloadQR}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ marginRight: '8px' }}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="7 10 12 15 17 10" />
            <line x1="12" y1="15" x2="12" y2="3" />
          </svg>
          Download QR Code
        </DownloadButton>

        <HelpText>
          Scan this QR code or share the link to let users access your form
        </HelpText>
      </ShareContainer>
    </PopupComponent>
  );
};

export default ShareFormPopup;

const ShareContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 16px 0;
`;

const QRSection = styled.div`
  background: #ffffff;
  padding: 16px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
`;

const FormNameLabel = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
  text-align: center;
`;

const LinkSection = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;
  margin-bottom: 16px;
`;

const LinkInput = styled.input`
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  color: #374151;
  background: #f9fafb;
  cursor: text;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const CopyButton = styled.button<{ $copied: boolean }>`
  display: flex;
  align-items: center;
  padding: 10px 16px;
  background: ${(props) => (props.$copied ? '#10b981' : '#ffffff')};
  color: ${(props) => (props.$copied ? '#ffffff' : '#374151')};
  border: 1px solid ${(props) => (props.$copied ? '#10b981' : '#d1d5db')};
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: ${(props) => (props.$copied ? '#059669' : '#f3f4f6')};
  }
`;

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px 16px;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: #2563eb;
  }
`;

const HelpText = styled.p`
  margin: 16px 0 0 0;
  font-size: 13px;
  color: #6b7280;
  text-align: center;
`;
